export const PRESET_REMARKS: string[] = [
  "dtc-chilled-ineligible",
  "etf-non-us",
  "etf-us",
  "exchange-not-tradeable",
  "hard-to-fill",
  "non-us-retail-restricted",
  "ptp",
].sort();

export function sanitizeRemarkChars(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]/g, "");
}

export function parseRemarks(remarks: string | null | undefined): string[] {
  if (!remarks) return [];

  const seen = new Set<string>();
  const parsed: string[] = [];
  for (const part of remarks.split(",")) {
    const remark = part.trim().toLowerCase();
    if (remark && !seen.has(remark)) {
      seen.add(remark);
      parsed.push(remark);
    }
  }
  return parsed;
}

export interface ParsedConIds {
  conIds: number[];
  invalidValues: string[];
}

export function parseConIds(value: string): ParsedConIds {
  const seen = new Set<number>();
  const conIds: number[] = [];
  const invalidValues: string[] = [];

  for (const token of value.split(/[\s,]+/).filter(Boolean)) {
    if (!/^\d+$/.test(token)) {
      invalidValues.push(token);
      continue;
    }

    const conId = Number(token);
    if (!Number.isSafeInteger(conId) || conId <= 0) {
      invalidValues.push(token);
      continue;
    }

    if (!seen.has(conId)) {
      seen.add(conId);
      conIds.push(conId);
    }
  }

  return { conIds, invalidValues };
}

export interface ParsedRemarkLines {
  remarks: string[];
  invalidValues: string[];
}

export function parseRemarkLines(value: string): ParsedRemarkLines {
  const seen = new Set<string>();
  const remarks: string[] = [];
  const invalidValues: string[] = [];

  for (const line of value.split(/\r?\n/)) {
    const remark = line.trim().toLowerCase();
    if (!remark) continue;
    if (!/^[a-z0-9-]+$/.test(remark)) {
      invalidValues.push(line.trim());
      continue;
    }
    if (!seen.has(remark)) {
      seen.add(remark);
      remarks.push(remark);
    }
  }

  return { remarks, invalidValues };
}

export function applyRemarkChanges(
  currentRemarks: string | null | undefined,
  requestedRemarks: string[],
  action: "add" | "remove"
): string | null {
  const current = parseRemarks(currentRemarks);

  if (action === "add") {
    const existing = new Set(current);
    for (const remark of requestedRemarks) {
      if (!existing.has(remark)) {
        existing.add(remark);
        current.push(remark);
      }
    }
  } else {
    const removals = new Set(requestedRemarks);
    return current.filter((remark) => !removals.has(remark)).join(",") || null;
  }

  return current.join(",") || null;
}
